"use client";

import { useCallback, type RefObject } from "react";

/** Breathing room between the header and the first row of cards. */
const SCROLL_GAP = 16;

/**
 * scrollY at which the theme pins the site header — `public/main/js/main.js`
 * adds `.sticky` to `.sticky-wrapper` once `topPos > 500`.
 *
 * Pinning switches the header to `position: fixed`, taking it out of normal
 * flow, so everything below it shifts up by its own height. A scroll target
 * measured on one side of this line is wrong on the other, which is why the
 * threshold has to be part of the calculation rather than a fixed offset.
 */
const STICKY_ACTIVATES_AT = 500;

/**
 * Bring the top of a listing back into view after its contents change.
 *
 * Not `scrollIntoView({ block: "start" })`: that aligns with the very top of
 * the viewport, so the first row of cards ends up hidden behind the pinned
 * site header. The header's height differs per breakpoint, so it is measured
 * rather than hard-coded.
 */
export default function useScrollToListTop(ref: RefObject<HTMLElement | null>) {
  return useCallback(() => {
    const list = ref.current;
    if (!list) return;

    const header = document.querySelector<HTMLElement>(".sticky-wrapper");
    const headerHeight = header?.offsetHeight ?? 0;
    const listTop = list.getBoundingClientRect().top + window.scrollY;

    // Below the pin threshold the header sits in flow above the listing and
    // covers nothing, so only the gap needs clearing.
    let top = listTop - SCROLL_GAP;

    if (top > STICKY_ACTIVATES_AT) {
      top = listTop - headerHeight - SCROLL_GAP;

      // Guard the narrow band where neither regime is self-consistent: stop
      // just short of pinning rather than tucking the listing behind it.
      if (top <= STICKY_ACTIVATES_AT) top = STICKY_ACTIVATES_AT;
    }

    window.scrollTo({
      top: Math.max(top, 0),
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  }, [ref]);
}
